'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { normalizeToStartOfDayNY, normalizeToEndOfDayNY } from '@/lib/timezone';

export interface ChartFilterState {
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  organizationId: string | null;
  productId: string | null;
  patientState: string | null;
}

// Serializable version for storage
interface StoredChartFilter {
  dateRange: {
    from: string | null;  // ISO date string YYYY-MM-DD
    to: string | null;
  };
  organizationId: string | null;
  productId: string | null;
  patientState: string | null;
}

interface ChartFiltersContextType {
  getChartFilters: (chartId: string) => ChartFilterState;
  setChartFilters: (chartId: string, filters: Partial<ChartFilterState>) => void;
  clearChartFilters: (chartId: string) => void;
  clearAllChartFilters: () => void;
  hasActiveFilters: (chartId: string) => boolean;
  getActiveFilterCount: (chartId: string) => number;
  isHydrated: boolean;
  isSaving: boolean;
  // Version number that increments on every filter change - use for forcing re-fetches
  filterVersion: number;
}

const defaultFilterState: ChartFilterState = {
  dateRange: { from: null, to: null },
  organizationId: null,
  productId: null,
  patientState: null,
};

const ChartFiltersContext = createContext<ChartFiltersContextType | undefined>(undefined);

const STORAGE_KEY = 'chart-filters';
const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

// Helper to convert Date to YYYY-MM-DD string
function dateToString(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Helper to convert YYYY-MM-DD string to Date
function stringToDate(str: string | null, isEndOfDay = false): Date | null {
  if (!str) return null;
  const [year, month, day] = str.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return isEndOfDay ? normalizeToEndOfDayNY(date) : normalizeToStartOfDayNY(date);
}

// Convert stored format to runtime format
function storedToRuntime(stored: StoredChartFilter): ChartFilterState {
  return {
    dateRange: {
      from: stringToDate(stored.dateRange.from),
      to: stringToDate(stored.dateRange.to, true),
    },
    organizationId: stored.organizationId,
    productId: stored.productId,
    patientState: stored.patientState,
  };
}

// Convert runtime format to stored format
function runtimeToStored(runtime: ChartFilterState): StoredChartFilter {
  return {
    dateRange: {
      from: dateToString(runtime.dateRange.from),
      to: dateToString(runtime.dateRange.to),
    },
    organizationId: runtime.organizationId,
    productId: runtime.productId,
    patientState: runtime.patientState,
  };
}

export function ChartFiltersProvider({ children }: { children: ReactNode }) {
  const [chartFilters, setChartFiltersState] = useState<Record<string, ChartFilterState>>({});
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [filterVersion, setFilterVersion] = useState(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get auth token from localStorage
  const getAuthToken = useCallback(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('cv_access_token');
  }, []);

  // Load filters from API
  const loadFromAPI = useCallback(async (token: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/api/profile/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.preferences?.chart_filters) {
          const stored = data.preferences.chart_filters as Record<string, StoredChartFilter>;
          const runtime: Record<string, ChartFilterState> = {};
          Object.entries(stored).forEach(([chartId, filter]) => {
            runtime[chartId] = storedToRuntime(filter);
          });
          setChartFiltersState(runtime);
          return true;
        }
      }
    } catch (error) {
      console.error('Failed to load chart filters from API:', error);
    }
    return false;
  }, []);

  // Save filters to API
  const saveToAPI = useCallback(async (filters: Record<string, ChartFilterState>) => {
    const token = getAuthToken();
    if (!token) return;

    const stored: Record<string, StoredChartFilter> = {};
    Object.entries(filters).forEach(([chartId, filter]) => {
      stored[chartId] = runtimeToStored(filter);
    });

    try {
      await fetch(`${API_BASE}/api/profile/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ chart_filters: stored }),
      });
    } catch (error) {
      console.error('Failed to save chart filters to API:', error);
    }
  }, [getAuthToken]);

  // Hydrate from API first, fallback to localStorage
  useEffect(() => {
    const hydrate = async () => {
      const token = getAuthToken();

      // Try API first if authenticated
      if (token) {
        const loaded = await loadFromAPI(token);
        if (loaded) {
          setIsHydrated(true);
          return;
        }
      }

      // Fallback to localStorage
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as Record<string, StoredChartFilter>;
          const runtime: Record<string, ChartFilterState> = {};
          Object.entries(parsed).forEach(([chartId, filter]) => {
            runtime[chartId] = storedToRuntime(filter);
          });
          setChartFiltersState(runtime);
        } catch {
          // Ignore parse errors
        }
      }
      setIsHydrated(true);
    };

    hydrate();
  }, [getAuthToken, loadFromAPI]);

  // Debounced save to localStorage and API
  useEffect(() => {
    if (!isHydrated) return;

    // Convert to stored format
    const stored: Record<string, StoredChartFilter> = {};
    Object.entries(chartFilters).forEach(([chartId, filter]) => {
      stored[chartId] = runtimeToStored(filter);
    });

    // Save to localStorage immediately
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));

    // Debounce API save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveToAPI(chartFilters);
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [chartFilters, isHydrated, saveToAPI]);

  const getChartFilters = useCallback((chartId: string): ChartFilterState => {
    return chartFilters[chartId] || defaultFilterState;
  }, [chartFilters]);

  const setChartFilters = useCallback((chartId: string, filters: Partial<ChartFilterState>) => {
    setChartFiltersState(prev => {
      const current = prev[chartId] || defaultFilterState;
      const updated = { ...current, ...filters };

      // If setting dateRange, normalize the dates
      if (filters.dateRange) {
        updated.dateRange = {
          from: filters.dateRange.from
            ? normalizeToStartOfDayNY(filters.dateRange.from)
            : null,
          to: filters.dateRange.to
            ? normalizeToEndOfDayNY(filters.dateRange.to)
            : null,
        };
      }

      return { ...prev, [chartId]: updated };
    });
    // Increment version to trigger re-fetches
    setFilterVersion(v => v + 1);
  }, []);

  const clearChartFilters = useCallback((chartId: string) => {
    setChartFiltersState(prev => {
      const newState = { ...prev };
      delete newState[chartId];
      return newState;
    });
    // Increment version to trigger re-fetches
    setFilterVersion(v => v + 1);
  }, []);

  const clearAllChartFilters = useCallback(() => {
    setChartFiltersState({});
    localStorage.removeItem(STORAGE_KEY);
    setFilterVersion(v => v + 1);
  }, []);

  const hasActiveFilters = useCallback((chartId: string): boolean => {
    const filters = chartFilters[chartId];
    if (!filters) return false;
    return !!(
      filters.dateRange.from ||
      filters.dateRange.to ||
      filters.organizationId ||
      filters.productId ||
      filters.patientState
    );
  }, [chartFilters]);

  const getActiveFilterCount = useCallback((chartId: string): number => {
    const filters = chartFilters[chartId];
    if (!filters) return 0;
    let count = 0;
    if (filters.dateRange.from || filters.dateRange.to) count++;
    if (filters.organizationId) count++;
    if (filters.productId) count++;
    if (filters.patientState) count++;
    return count;
  }, [chartFilters]);

  return (
    <ChartFiltersContext.Provider
      value={{
        getChartFilters,
        setChartFilters,
        clearChartFilters,
        clearAllChartFilters,
        hasActiveFilters,
        getActiveFilterCount,
        isHydrated,
        isSaving,
        filterVersion,
      }}
    >
      {children}
    </ChartFiltersContext.Provider>
  );
}

export function useChartFilters() {
  const context = useContext(ChartFiltersContext);
  if (context === undefined) {
    throw new Error('useChartFilters must be used within a ChartFiltersProvider');
  }
  return context;
}
